import messagesInEnglish from 'app/lang/en.json';
import messagesInBritishEnglish from 'app/lang/en-GB.json';
import messagesInCanadianEnglish from 'app/lang/en-CA.json';
import messagesInIrishEnglish from 'app/lang/en-IE.json';
import messagesInAustralianEnglish from 'app/lang/en-AU.json';
import messagesInGerman from 'app/lang/de-DE.json';
import messagesInSpanish from 'app/lang/es-ES.json';
import messagesInFrench from 'app/lang/fr-FR.json';
import messagesInCanadianFrench from 'app/lang/fr-CA.json';
import messagesInItalian from 'app/lang/it-IT.json';
import messagesInNorwegian from 'app/lang/nb-NO.json';
import messagesInDutch from 'app/lang/nl-NL.json';
import messagesInPolish from 'app/lang/pl.json';
import messagesInPortuguese from 'app/lang/pt-PT.json';
import messagesInHungarian from 'app/lang/hu-HU.json';
import messagesInSlovak from 'app/lang/sk-SK.json';
import { flatten } from 'app/shared/utils/flatten';
import { useMemo } from 'react';

export const DEFAULT_LOCALE = 'en';

/**
 * Keyed by BOTH the regional tag and the bare language.
 *
 * The bare key is what makes a language reachable at all: browsers routinely send a primary tag with
 * no region — Chrome set to German reports `de` — and a building may be configured for a region we
 * ship no file for (`de-AT`, `fr-BE`, `es-MX`, `nl-BE`). Without it, both fall through to English
 * while the language's own catalogue sits in the bundle, translated and unused.
 *
 * `fr` resolves to fr-FR and `pt` to pt-PT; the regional file stays the exact match, so `fr-CA` is
 * unaffected.
 */
const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.miniappMindbody],
  ['en-gb', messagesInBritishEnglish.miniappMindbody],
  ['en-ca', messagesInCanadianEnglish.miniappMindbody],
  ['en-ie', messagesInIrishEnglish.miniappMindbody],
  ['en-au', messagesInAustralianEnglish.miniappMindbody],
  ['de-de', messagesInGerman.miniappMindbody],
  ['de', messagesInGerman.miniappMindbody],
  ['es-es', messagesInSpanish.miniappMindbody],
  ['es', messagesInSpanish.miniappMindbody],
  ['fr-fr', messagesInFrench.miniappMindbody],
  ['fr', messagesInFrench.miniappMindbody],
  ['fr-ca', messagesInCanadianFrench.miniappMindbody],
  ['it-it', messagesInItalian.miniappMindbody],
  ['it', messagesInItalian.miniappMindbody],
  ['nb-no', messagesInNorwegian.miniappMindbody],
  ['nb', messagesInNorwegian.miniappMindbody],
  ['no', messagesInNorwegian.miniappMindbody],
  ['nl-nl', messagesInDutch.miniappMindbody],
  ['nl', messagesInDutch.miniappMindbody],
  ['pl', messagesInPolish.miniappMindbody],
  ['pt-pt', messagesInPortuguese.miniappMindbody],
  ['pt', messagesInPortuguese.miniappMindbody],
  ['hu-hu', messagesInHungarian.miniappMindbody],
  ['hu', messagesInHungarian.miniappMindbody],
  ['sk-sk', messagesInSlovak.miniappMindbody],
  ['sk', messagesInSlovak.miniappMindbody],
]);

/**
 * A tag can name a real catalogue and still be unusable by Intl: `en-us@posix` resolves its language
 * fine, but `new Intl.NumberFormat('en-us@posix')` throws a RangeError, as do `''` and `C`. The value
 * returned here becomes the IntlProvider locale and therefore the argument to every formatter, so it
 * has to be one Intl can consume.
 */
const isIntlSafe = (tag: string): boolean => {
  try {
    return Boolean(new Intl.NumberFormat(tag)) && Boolean(new Intl.DateTimeFormat(tag));
  } catch {
    return false;
  }
};

export const getAvailableLocale = (userLocale: string): [string, string] => {
  // Normalize case + separator before the lookup: map keys are lower-case hyphenated, but a host may
  // pass a canonical ('hu-HU', region-capitalised by Intl.getCanonicalLocales) or underscore ('hu_HU')
  // tag, and matching the raw value drops every such tag to English.
  const normalized = userLocale.toLowerCase().replace(/_/g, '-');

  if (messagesMap.has(normalized)) {
    return [normalized, isIntlSafe(normalized) ? normalized : DEFAULT_LOCALE];
  }

  const [lang] = normalized.split('-');

  if (messagesMap.has(lang)) {
    // Keep the reader's region so numbers and dates format for them, but only where Intl can consume
    // it; otherwise fall back to the bare language, which always can.
    return [lang, isIntlSafe(normalized) ? normalized : lang];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData => useMemo(() => {
  const [messagesKey, locale] = getAvailableLocale(userLocale);

  return {
    locale,
    messages: flatten(messagesMap.get(messagesKey)) as Record<string, string>,
  };
}, [userLocale]);
